import { SearchSelect, SearchSelectOptionProps } from "@alm/flow-ds/components";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonPropertyProps } from "schemas/elements";

type Props = Omit<CommonPropertyProps, "value"> & {
	value: string[] | { [k: string]: string[] };
	nextProp: string;
	options?: SearchSelectOptionProps[];
	loading: boolean;
	disabledPlaceholder: string;
};

export default ({
	updateStore,
	field,
	required,
	value,
	desc,
	validation,
	elementIndex,
	nextProp,
	id,
	options,
	loading,
	disabledPlaceholder,
	nestedField,
}: Props) => {
	const { t } = useTranslation();

	const formattedValues = useMemo(() => {
		if (Array.isArray(value)) {
			return value.map(v => ({ value: v, label: v }));
		} else if (nextProp) {
			return value[nextProp].map(v => ({ value: v, label: v }));
		} else return [];
	}, [nextProp, value]);

	const [selectedValues, setSelectedValues] = useState<
		SearchSelectOptionProps[] | null
	>(formattedValues);

	const [input, setInput] = useState("");

	const handleUpdateStore = useCallback(
		vals => {
			const formatArray = vals?.map(({ value }: { value: string }) => {
				return value;
			});
			updateStore({
				elementIndex,
				property: field,
				nestedField,
				value: nextProp
					? {
							...value,
							[nextProp]: formatArray,
					  }
					: formatArray,
			});
		},
		[elementIndex, field, nestedField, nextProp, updateStore, value]
	);

	const handleChange = useCallback(newVals => {
		setSelectedValues(newVals);
	}, []);

	const handleKeyDown = useCallback(
		event => {
			if (event.key === "Enter") {
				event.preventDefault();
				const value = input.replace(/\s/g, "");
				if (value) {
					const newVal = {
						label: input,
						value: input,
					};

					if (
						selectedValues?.some(
							({ value }) => value === newVal.value
						)
					)
						return;

					selectedValues?.length
						? setSelectedValues([...selectedValues, newVal])
						: setSelectedValues([newVal]);

					setInput("");
				}
			}
		},
		[input, selectedValues]
	);

	return (
		<SearchSelect
			hint={t(`formDesigner.elements.${desc}`)}
			id={id}
			onChange={handleChange}
			label={
				nextProp
					? t(`formDesigner.properties.${nextProp}`)
					: t(`formDesigner.properties.${field}`)
			}
			required={required}
			value={selectedValues}
			placeholder={
				disabledPlaceholder && !options
					? disabledPlaceholder
					: disabledPlaceholder && options
					? t("formDesigner.placeholders.select")
					: t("formDesigner.placeholders.stringArray")
			}
			multiselect={true}
			inputValue={input}
			onInputChange={setInput}
			onBlur={() => handleUpdateStore(selectedValues)}
			className={
				validation && required && !selectedValues?.length
					? " invalid"
					: ""
			}
			{...(!options
				? {
						dropdownIndicator: false,
						menuIsOpen: false,
						onKeyDown: handleKeyDown,
				  }
				: { options, loading })}
		/>
	);
};
